import { Subscription } from 'rxjs/Subscription';
import { Injectable } from '@angular/core';
import { StageService } from '../stage.service';
import { BehaviorSubject } from 'rxjs';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { RailingColorsService } from '../railing/services/railing-colors.service';
import { map } from 'rxjs/operators/map';
import { ProductsService } from '../products.service';
import { from } from 'rxjs/observable/from';
import { filter, tap } from 'rxjs/operators';
import { Color } from '../../models/2d3d/color';
import { AwningTexture } from './models/awning-texture';
import {SceneryService} from '../scenery.service';
declare const THREE: any;

@Injectable()
export class AwningColorTextureService {
  private _textures: AwningTexture[] = [
    {
      name: 'clear',
      thumbnail: '/assets/images/2_DeckTab/awnings/glass/VT_icons_awn_clear.png',
      thumbnailSelected: '/assets/images/2_DeckTab/awnings/glass/VT_icons_awn_clear_on.png',
      texture: '/assets/images/texture/awning/clear_glass.png',
      selected: true
    },
    {
      name: 'textured',
      thumbnail: '/assets/images/2_DeckTab/awnings/glass/VT_icons_awn_frosted.png',
      thumbnailSelected: '/assets/images/2_DeckTab/awnings/glass/VT_icons_awn_frosted_on.png',
      texture: '/assets/images/texture/awning/textured_glass.png',
      selected: false
    },
    {
      name: 'gray tint',
      thumbnail: '/assets/images/2_DeckTab/awnings/glass/VT_icons_awn_gray.png',
      thumbnailSelected: '/assets/images/2_DeckTab/awnings/glass/VT_icons_awn_gray_on.png',
      texture: '/assets/images/texture/awning/grey_tinted.png',
      selected: false
    }
  ];
  private _loader = new THREE.TextureLoader();
  private _selectedFrameColor: Color;
  private _selectedGlassTexture: AwningTexture;
  selectedFrameColor = new BehaviorSubject<Color>(null);
  selectedGlassTexture = new BehaviorSubject<AwningTexture>(null);
  colors = new BehaviorSubject<Color[]>(null);
  awningAddedSubscription: Subscription;

  constructor(
    private stage: StageService,
    private scenery: SceneryService,
    private colorsService: RailingColorsService,
    private productsService: ProductsService
  ) {
    this.glassTexture = this._textures[0];
    this._getColors();
    this._onAwningAdded();
  }

  get textures() {
    return this._textures;
  }

  set frameColor(color: Color) {
    this.selectedFrameColor.next(color);
    this._selectedFrameColor = color;
  }

  set glassTexture(texture: AwningTexture) {
    this.selectedGlassTexture.next(texture);
    this._selectedGlassTexture = texture;
  }

  get frameColor() {
    return this._selectedFrameColor;
  }

  get glassTexture() {
    return this._selectedGlassTexture;
  }

  private _loadColors() {
    return this.colorsService
      .getColors()
      .pipe(map(colors => this._correctPathToColorsTexture(colors)));
  }

  private _correctPathToColorsTexture(colors) {
    return colors.reduce((colors, color) => {
      if (color.texture) {
        color.texture = color.texture.replace('upload', 'download');
      }
      colors.push(color);
      return colors;
    }, []);
  }

  private _getColors() {
    forkJoin(
      this.colorsService.getColorGroups(),
      this._loadColors(),
      from(this.productsService.getBaseAwnings())
    )
      .pipe(
        map(([groups, colors, awnings]) => {
          const awningColorGroups: any = new Set(awnings[0].colors);
          const colorIds = new Set(
            groups
              .filter(cg => awningColorGroups.has(cg.id))
              .map(cg => cg.colors)
              .reduce((acc, val) => acc.concat(val), [])
          );
          const filteredColors = colors.filter(c => colorIds.has(c.id));
          this.colors.next(filteredColors);
          this.frameColor = filteredColors.find(c => c.name === 'Black');
        })
      )
      .subscribe();
  }

  setFrameColorForOneAwning(obj, color = this.frameColor) {
    const materialConfig = {
      color: new THREE.Color(color.rgb || 0xffffff),
      texture: color.texture
        ? this._loader.load(color.texture, texture => {
            texture.wrapT = texture.wrapS = THREE.RepeatWrapping;
            texture.needsUpdate = true;
          })
        : null
    };

    const frame = obj.getObjectByName('Frame');
    frame.material.color.copy(materialConfig.color);
    frame.material.map = materialConfig.texture;
    frame.material.needsUpdate = true;
  }

  setGlassTextureForOneAwning(obj, textureObject = this.glassTexture) {
    const { texture } = textureObject;
    const glass = obj.getObjectByName('Glass');
    const glass_material = glass.material;
    glass_material.map = this._loader.load(texture);
    glass_material.envMap = this.scenery.cubeMap();
    glass_material.shininess = 100;
    glass_material.map.needsUpdate = true;
    glass_material.needsUpdate = true;
    console.log(glass_material);
  }

  private _onAwningAdded() {
    this.awningAddedSubscription = this.stage.objectAdded
      .pipe(
        filter(obj => obj.name === 'Awning'),
        map(obj => {
          // console.log('obj added', obj);
          this.setFrameColorForOneAwning(obj, this.frameColor);
          this.setGlassTextureForOneAwning(obj, this.glassTexture);
        })
      )
      .subscribe();
  }

  setFrameColor(color) {
    this.frameColor = color;
    const compoundFloor = this.stage.getCompoundFloor();
    compoundFloor.traverse(c => {
      const entity = c.userData.entity;
      if (entity && entity.productType === 'Awning') {
        this.setFrameColorForOneAwning(c, color);
      }
    });
  }

  setGlassTexture(textureObject) {
    this.glassTexture = textureObject;
    const compoundFloor = this.stage.getCompoundFloor();
    compoundFloor.traverse(c => {
      const entity = c.userData.entity;
      if (entity && entity.productType === 'Awning') {
        this.setGlassTextureForOneAwning(c, textureObject);
      }
    });
  }

  ngOnDestroy() {
    if (this.awningAddedSubscription) this.awningAddedSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/services/awning/awning-color-texture.service.ts